<script setup lang="ts">
import { computed } from 'vue';
import { computedAsync } from '@vueuse/core';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';

import { AppButton, AppPreferencesTags } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import type {
  ItineraryAccommodationLoaderSegment,
  ItineraryAccommodationSegment,
} from '../../../models';
import {
  isUnavailableAccommodationSegment,
  isAlternativeRoomSuggestionAccommodationSegment,
  UnavailabilityReason,
  isItineraryAccommodationLoaderSegment,
} from '../../../models';

const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

interface AccommodationSegmentHeaderProps {
  segment: ItineraryAccommodationSegment | ItineraryAccommodationLoaderSegment;
  printMode: boolean;
}

const props = defineProps<AccommodationSegmentHeaderProps>();

interface AvailableAccommodationSegmentEmits {
  (eventName: 'add-filter'): void;
  (eventName: 'remove-filter', id: HotelFilter): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'toggle-accommodation'): void;
  (eventName: 'see-more-hotels'): void;
}

const emit = defineEmits<AvailableAccommodationSegmentEmits>();

const shouldShowPreferencesTags = computed<boolean>(() => {
  if (isItineraryAccommodationLoaderSegment(props.segment)) {
    return true;
  }
  if (isAlternativeRoomSuggestionAccommodationSegment(props.segment)) {
    return false;
  }
  if (isUnavailableAccommodationSegment(props.segment)) {
    return props.segment.reasons.includes(UnavailabilityReason.SelectedFilters);
  }
  return true;
});

const isSeeAllHotelsButtonExperiment = computedAsync(
  () => isExperimentAsync('see_all_hotel_button'),
  false
);
</script>

<template>
  <div
    :class="[
      'accommodation-segment-header',
      {
        'see-all-hotels-button-experiment':
          isSeeAllHotelsButtonExperiment && browserStore.isLargerThan1199px,
        'show-preferences-tags': shouldShowPreferencesTags,
      },
    ]"
  >
    <template v-if="!props.printMode">
      <AppPreferencesTags
        v-if="shouldShowPreferencesTags"
        :tags="props.segment.filters"
        :is-mobile="browserStore.isSmallerThan1200px"
        :hide-selected-filters="
          isSeeAllHotelsButtonExperiment && browserStore.isLargerThan1199px
        "
        :add-preferences-text="$t('filter.add_filters')"
        :remove-icon-aria-label="$t('a11y.remove_filter')"
        :should-hide-add-button="true"
        :show-filters-aria-label="$t('a11y.show_filters')"
        class="preferences-tags"
        @add-filter="
          statsigEventsStore.logTripOverviewFilterButtonClicked();
          emit('add-filter');
        "
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event as HotelFilter)"
      />

      <div
        v-if="isSeeAllHotelsButtonExperiment && browserStore.isLargerThan1199px"
        class="see-more-hotels-button"
      >
        <AppButton
          state="enabled"
          size="small"
          type="secondary"
          aria-label="See more hotels"
          :left-icon="{
            size: '18',
            name: 'list',
          }"
          @click="emit('see-more-hotels')"
        >
          {{
            $t(
              'planner.experiments.show_more_hotels.explore_more_accommodation'
            )
          }}
        </AppButton>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.accommodation-segment-header {
  width: 100%;

  display: grid;
  grid-template-columns: min-content min-content;
  grid-template-rows: auto;
  align-items: center;
  margin-left: var(--spacing-12);

  &.see-all-hotels-button-experiment {
    &.show-preferences-tags {
      grid-template-columns: 1fr 1fr;
    }
    grid-template-columns: 1fr;

    .preferences-tags {
      grid-column: 1/1;
      grid-row: 1/-1;
    }
  }

  .preferences-tags {
    grid-column: 3/4;
    grid-row: 1/-1;
  }

  @include for-less-or-equal-than-599-px {
    display: block;

    grid-template-columns: auto auto;
    grid-template-rows: auto auto;
  }

  .see-more-hotels-button {
    display: flex;
    align-items: center;
    justify-self: flex-end;
    @include for-less-or-equal-than-599-px {
      display: none;
    }
  }
}
</style>
